// React
import { useContext, useEffect } from "react"

// Validation
import { Yup } from "@/lib/forms"
import { validateIBAN } from "ibantools"

// i18n
import { useTrans, Trans } from "@/i18n"

// UI
import { Heading } from "@/components/Typography"
import { CardBody, CardWrapper } from "@/components/Card"
import { Tooltip } from "@/components/Tooltip"
import { RecipientStatusBanner } from "../RecipientStatusBanner"

// Icons
import { FiChevronRight, FiInfo } from "@/lib/icons"

// Forms
import { FormikProvider, useFormik, mapRestErrorsToFormik } from "@/lib/formik"
import {
	FormikError,
	FormikErrors,
	FormikInput,
	FormikSubmitButton,
	FormGroup,
	FormikLabel,
} from "@/components/form-controls"

// Queries
import { useBankDetailsQuery } from "@/api/graphql"
import { useApiV1InvestorIbanPartialUpdate } from "@/api/rest/generated/api/api"
import {
	ApiV1InvestorIbanPartialUpdate200,
	ApiV1InvestorIbanPartialUpdate400,
} from "@/api/rest/generated/@types"

// Context
import {
	ControlStatusEnum,
	useHiddenGetParamsContext,
} from "@/context/useHiddenGetParams"
import { RedirectContext } from "@/context/redirectContext"
import { useToasts } from "@/context/useToasts"

// Overlay
import { MangopayGate } from "@/pages/investor/pages/_components/MangopayGate"

// Sentry
import { captureMessage } from "@/lib/sentry"

// Security
import { rewriteMangoPayReturnUrlToCurrentUrl } from "@/lib/security"
import { EXTERNAL_PLATFORM_ZONHUB_MANGOPAY_FAQ_URL } from "@/lib/env"

/**
 * BankDetails
 * @returns
 */
export const BankDetails = () => {
	// Translations
	const t = useTrans("profile")

	// Query
	const { data, isLoading } = useBankDetailsQuery()

	// Context
	const toasts = useToasts()
	const { redirect } = useContext(RedirectContext)
	const { controlStatus } = useHiddenGetParamsContext()

	// When the user returns from Mangopay's SCA flow, controlStatus reflects
	// the outcome; surface it as a toast. Only `controlStatus` is in the deps:
	// `toasts` and `t` come from hook calls that return a fresh reference each
	// render, so including them would re-fire the toast on every render.
	useEffect(() => {
		// Stable id per status — addToast dedupes by id, so any spurious
		// re-fires (StrictMode double-invoke, HMR remount, parent re-render)
		// collapse to a single toast instead of stacking.
		if (controlStatus === ControlStatusEnum.VALIDATED) {
			toasts.addToast({
				variant: "success",
				id: "bank-details-sca-validated",
				text: t("profile:personal_details.success_message"),
			})
		}

		if (controlStatus === ControlStatusEnum.FAILED) {
			toasts.addToast({
				variant: "alert",
				id: "bank-details-sca-failed",
				text: t("profile:personal_details.failed_message"),
			})
		}
	}, [controlStatus])

	// Mutation
	const updateIban = useApiV1InvestorIbanPartialUpdate({
		mutation: {
			onSuccess: async (response: ApiV1InvestorIbanPartialUpdate200) => {
				// USER_PRESENT SCA: backend returns a Mangopay redirect URL the
				// user must visit to complete the challenge.
				if (response?.verification_url) {
					redirect(
						rewriteMangoPayReturnUrlToCurrentUrl(
							response.verification_url,
						),
					)
				} else {
					// If no verification url, show a success toast
					toasts.addToast({
						variant: "success",
						id: "bank-details-success",
						text: t("profile:personal_details.success_message"),
					})
				}
			},
			onError: (response: ApiV1InvestorIbanPartialUpdate400) => {
				const mapped = mapRestErrorsToFormik(response, form)
				if (mapped) {
					// Also surface the first mapped message as a toast, since
					// this form has only one field and the user may not notice
					// the inline error under the input.
					const ibanError = response?.iban?.[0]
					if (ibanError) {
						toasts.addToast({
							variant: "alert",
							id: "bank-details-alert",
							text: ibanError,
						})
					}
				}
				// Report every failure — mapped field errors also signal a
				// rejected recipient, which blocks payouts.
				captureMessage("Saving IBAN failed", {
					level: "error",
					extra: { response },
				})
			},
		},
	})

	// The schema is what re-validates the field on change and clears any
	// stale server error left by `mapRestErrorsToFormik` — otherwise the
	// submit button stays disabled after a rejected save.
	const form = useFormik({
		enableReinitialize: true,
		initialValues: {
			full_name: data?.me?.full_name,
			iban: "",
		},
		validationSchema: Yup.object({
			iban: Yup.string()
				.required("profile:bank_details.form_errors.required")
				.test(
					"iban-valid",
					"profile:bank_details.form_errors.invalid_account_number",
					(value) =>
						!value || validateIBAN(value.replace(/\s/g, "")).valid,
				),
		}),
		onSubmit: async (values) => {
			await updateIban.mutateAsync({
				data: {
					iban: values.iban,
				},
			})
		},
	})

	// Show loading state if data is loading
	if (isLoading) {
		return null
	}

	const recipientStatus =
		data?.me?.mangopay_user?.sca?.recipient_status ?? null

	return (
		<MangopayGate>
			<CardWrapper>
				<FormikProvider value={form}>
					<form onSubmit={form.handleSubmit}>
						<CardBody>
							<Heading
								as="h2"
								styleAs="h5"
								className="mb-3 sm:truncate"
							>
								{t("profile:bank_details.title")}
							</Heading>
							{recipientStatus && (
								<RecipientStatusBanner
									status={recipientStatus}
									maskedIban={
										data?.me?.investor?.masked_iban ?? null
									}
								/>
							)}
							<FormGroup
								heading={
									<>
										<p className="whitespace-break-spaces">
											{t("profile:bank_details.copy")}
											<br /> <br />
											{t(
												"profile:bank_details.mangopay_copy",
											)}
										</p>

										{/** Logo */}
										<img
											src="/assets/partners/mangopay_logo.png"
											alt="Mangopay logo"
											className="w-1/2"
										/>

										{/** FAQ */}
										<p className="whitespace-break-spaces">
											<Trans
												i18nKey="investor:identity.copy.mangopay.copy.faq"
												components={{
													/* oxlint-disable jsx-a11y/anchor-has-content, jsx-a11y/control-has-associated-label */
													a: (
														<a
															className="text-secondary-700 font-medium"
															href={
																EXTERNAL_PLATFORM_ZONHUB_MANGOPAY_FAQ_URL
															}
															target="_blank"
															rel="noreferrer"
														/>
														/* oxlint-enable jsx-a11y/anchor-has-content, jsx-a11y/control-has-associated-label */
													),
												}}
											/>
										</p>
									</>
								}
							>
								<div
									className="w-fulls grid gap-6 2xl:grid-cols-2"
									data-testid="form-group"
								>
									{/** Name just as confirmation */}
									<div className="2xl:col-span-2">
										<FormikLabel
											htmlFor="full_name"
											className="flex"
										>
											<Tooltip
												content={
													<p>
														{t(
															"profile:personal_details.name.tooltip",
														)}
													</p>
												}
											>
												<span className="flex items-center">
													{t(
														"profile:personal_details.name.title",
													)}
													<FiInfo className="ml-2" />
												</span>
											</Tooltip>
										</FormikLabel>
										<FormikInput
											name="full_name"
											required
											className="mt-1 block w-full"
											readOnly
											disabled
											classNameWrapper="lg:w-1/2"
										/>
									</div>

									{/** Bank account number */}
									<div className="lg:w-1/2 2xl:col-span-2">
										<div className="flex flex-row gap-2">
											<div className="flex-1">
												<FormikLabel
													className="truncate"
													htmlFor="iban"
												>
													<span className="flex items-center">
														{t(
															"profile:bank_details.account_number.title",
														)}
													</span>
												</FormikLabel>
												<FormikInput
													placeholder={
														data?.me?.investor
															?.masked_iban ??
														undefined
													}
													type="iban"
													name="iban"
													required
													data-testid="input-iban"
													id="iban"
													aria-label="iban"
													className="mt-1 block w-full"
												/>
												{/* Backend validation errors come
											    back as plain translated text
											    via DRF; FormikError pipes them
											    through useTrans which is a
											    no-op for non-key strings. */}
												<FormikError
													field="iban"
													namespace="profile"
												/>
											</div>

											<FormikSubmitButton
												className="self-end"
												disableWhenPristine
											>
												{t(
													"profile:contact_details.confirm",
												)}
												<FiChevronRight className="ml-2" />
											</FormikSubmitButton>
										</div>

										{/** Errors */}
										<FormikErrors i18nNamespace="profile" />
									</div>
								</div>
							</FormGroup>
						</CardBody>
					</form>
				</FormikProvider>
			</CardWrapper>
		</MangopayGate>
	)
}
